import { TextToSpeech, type SpeechSynthesisVoice, type TTSOptions } from '@capacitor-community/text-to-speech';
import PQueue from 'p-queue';
import { useCallback, useEffect } from 'react';
import { atom, useRecoilValue, useSetRecoilState } from 'recoil';
import { logger } from '../log';
import { useTrackEvent } from '../tracking';
import { useInterval } from '../utils';
import { useSetting } from './settings';

const log = logger('speech');

const optionsAtom = atom<SpeechOptions>({
  key: 'speech:options',
  default: { voices: [], languages: [] },
});

const speechQueueAtom = atom<PQueue>({
  key: 'speech:queue',
  default: new PQueue({ concurrency: 1 }),
});

const optionsRefreshInterval = 1000 * 60;

export function useInitializeSpeech() {
  const update = useUpdateSpeechOptions();
  useEffect(update, [update]);
  useInterval(update, optionsRefreshInterval);
}

function useUpdateSpeechOptions() {
  const setOptions = useSetRecoilState(optionsAtom);

  return useCallback(() => {
    log.info('Updating speech options...');

    Promise.all([TextToSpeech.getSupportedVoices(), TextToSpeech.getSupportedLanguages()])
      .then(([{ voices }, { languages }]) => {
        log.info(
          `Available speech voices: ${voices
            .map((v) => v.name)
            .sort()
            .join(', ')}`
        );
        log.info(`Available speech languages: ${languages.sort().join(', ')}`);
        setOptions({
          voices,
          languages: languages.sort(),
        });
      })
      .catch((err) => console.error('Error getting speech voices and languages:', err));
  }, [setOptions]);
}

export function useSpeechOptions(): SpeechOptions {
  return useRecoilValue(optionsAtom);
}

export function useSpeak(): (text: string) => Promise<void> {
  const queue = useSpeechQueue();
  const handler = useSpeakHandler();

  return useCallback(async (text) => queue.add(() => handler(text)), [queue, handler]);
}

function useSpeakHandler(): (text: string) => Promise<void> {
  const config = useSpeechConfig();
  const trackEvent = useTrackEvent();

  return useCallback(
    async (text) => {
      log.info(`Speaking "${text}"`);
      trackEvent('Speech start');

      try {
        await TextToSpeech.speak({ ...config, text });
        log.info(`Done speaking "${text}".`);
        trackEvent('Speech done');
      } catch (e: unknown) {
        log.error(`Error speaking "${text}":`, e);
        trackEvent('Speech error');
      }
    },
    [config]
  );
}

function useSpeechConfig(): Pick<TTSOptions, 'voice' | 'lang' | 'rate'> {
  const options = useSpeechOptions();
  const speechVoiceURI = useSetting('speechVoiceURI');
  const voice = speechVoiceURI ? options.voices.findIndex((v) => v.voiceURI === speechVoiceURI) : undefined;
  const lang = useSetting('speechLanguage');
  const rate = useSetting('speechRate') ?? 1;

  return {
    voice: voice !== undefined && voice >= 0 ? voice : undefined,
    lang,
    rate,
  };
}

function useSpeechQueue() {
  return useRecoilValue(speechQueueAtom);
}

export type SpeechOptions = {
  voices: Array<SpeechSynthesisVoice>;
  languages: Array<string>;
};
